import React, { useState, useEffect, useRef, useCallback } from "react";

type Column<T> = {
  header: string;
  dataKey: keyof T;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
  align?: "left" | "center" | "right";
};

export interface CustomTableProps<T> {
  columns: Column<T>[];
  data: T[];
  showDivider?: boolean;
  rowClassName?: string;
  cellClassName?: string;
  onRowClick?: (row: T) => void;
  onLoadMore?: () => void;
  loading?: boolean;
}

const CustomTable = <T,>({
  columns,
  data,
  showDivider = true,
  rowClassName = "",
  cellClassName = "",
  onRowClick,
  onLoadMore,
  loading = false,
}: CustomTableProps<T>) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const renderCell = (value: T[keyof T]): React.ReactNode => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "✅ Yes" : "❌ No";
    if (typeof value === "object" && !React.isValidElement(value))
      return JSON.stringify(value);
    return value as React.ReactNode;
  };

  const handleScroll = useCallback(() => {
    if (!tableContainerRef.current || !onLoadMore) return;

    const { scrollTop, clientHeight, scrollHeight } = tableContainerRef.current;
    
    // Check if we're near the bottom (within 200px)
    if (scrollHeight - (scrollTop + clientHeight) < 200 && !loading) {
      onLoadMore();
    }
  }, [onLoadMore, loading]);

  // Attach scroll event listener
  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  // Initial loading state
  if (loading && data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[30vh]">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 border-solid"></div>
      </div>
    );
  }

  return (
    <div className="bg-white font-poppins rounded-xl overflow-hidden shadow-lg flex flex-col">
      <div 
        ref={tableContainerRef}
        className="overflow-y-auto h-[calc(100vh-100px)]" // Adjust height as needed
      >
        <table className="w-full min-w-[600px] border-collapse">
          {/* Table Head */}
          <thead className="sticky top-0 ">
            <tr className="text-white bg-[#444FBD] h-[60px] uppercase tracking-wide text-sm font-bold">
              {columns.map((col, index) => (
                <th
                  key={index}
                  className={`px-6 py-3 text-${col.align ?? "left"}`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody>
            {data.length > 0 ? (
              data.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  <tr
                    className={`text-gray-700 text-[14px] transition-all duration-200 ${
                      rowIndex % 2 === 0 ? "bg-gray-50" : "bg-white"
                    } hover:bg-gray-100 hover:shadow-md cursor-pointer ${rowClassName}`}
                    onClick={() => onRowClick?.(row)}
                  >
                    {columns.map((col, colIndex) => (
                      <td
                        key={colIndex}
                        className={`px-6 py-4 text-${col.align ?? "left"} ${cellClassName}`}
                      >
                        {col.render
                          ? col.render(row[col.dataKey], row)
                          : renderCell(row[col.dataKey])}
                      </td>
                    ))}
                  </tr>

                  {/* Dashed Divider */}
                  {showDivider && rowIndex !== data.length - 1 && (
                    <tr>
                      <td colSpan={columns.length}>
                        <div className="border-t border-dashed border-gray-300 mx-6"></div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center py-12 text-gray-500">
                  <div className="flex flex-col items-center">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="h-12 w-12 text-gray-300 mb-3" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={1.5} 
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" 
                      />
                    </svg>
                    <span className="font-medium text-base">No records found</span>
                    <span className="text-sm text-gray-400 mt-1">Try adjusting your search or filters</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Loading More Indicator */}
        {loading && (
          <div className="text-center py-4">
            <div className="inline-flex items-center">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#444FBD] mr-2"></div>
              <span className="text-gray-600">Loading more...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomTable;